import { useMutation, useQuery } from "@apollo/client"
import { Dialog } from "@headlessui/react"
import { type FailedUppyFile, type UploadedUppyFile } from "@uppy/core"
import cn from "classnames"
import { motion, AnimatePresence } from "framer-motion"
import React, { useRef, useCallback, useState, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import SVGInline from "react-svg-inline"
import invariant from "tiny-invariant"
import Uploader from "./uploader"
import { graphql } from "@/api"
import { type GenerationStatus, type Admin } from "@/api/graphql"
import FormForm from "@/common/components/form_form"
import LoadingAnimation from "@/common/components/loading"
import { useUppy } from "@/common/hooks/use_uppy"
import SentryReact from "@/common/sentry_client"
import storageLocal from "@/common/storage"
import { transitionTime } from "@/common/util"
import Images from "@/images/signomatic/excel_import"
import { setBasketStateDoc } from "@/menu/components/basket_modal"
import type SignText from "@/signomatic/app/objects/sign_text"
import Sign from "@/signomatic/app/sign"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import { ArrowRight, CloseLarge, CheckmarkSmall, PlayFill, Download, EditOutline, ArrowLeft } from "@/skylticons"

const generationStatusDoc = graphql(`
  query GenerationStatus($id: ID!) {
    generationStatus(id: $id) {
      id
      total
      done
      remaining
      finished
      saved
    }
  }
`)

const generationStatusSubDoc = graphql(`
  subscription GenerationStatusSub($id: ID!) {
    generationStatus(id: $id) {
      id
      total
      done
      remaining
      finished
      saved
    }
  }
`)

const generateSignsDoc = graphql(`
  mutation GenerateSigns($fileData: JSON!, $count: Int!, $type: String!, $template: String!) {
    generateSigns(fileData: $fileData, count: $count, type: $type, template: $template) {
      status {
        id
        total
        done
        remaining
        finished
        saved
      }

      errors {
        message
      }
    }
  }
`)

const generateSignsAbortDoc = graphql(`
  mutation GenerateSignsAbort($id: ID!) {
    generateSignsAbort(id: $id) {
      status {
        id
        total
        done
        remaining
        finished
        saved
      }

      errors {
        message
      }
    }
  }
`)

const currentAdminDoc = graphql(`
  query CurrentAdmin {
    currentAdmin {
      id
      username
      name
      developer
    }
  }
`)

interface HeaderProps {
  restrictClose?: boolean
  step: number
  setStep: (step: number) => void
  handleClose: () => void
  disabled?: boolean
}

const Header: React.FC<HeaderProps> = ({ restrictClose, step, setStep, handleClose, disabled }) => {
  const chooseDesign = step == 0
  const importSigns = [1, 2].includes(step)
  const { t } = useTranslation()

  const clickable = !restrictClose && !disabled

  return (
    <div className="header mb-6 flex h-[102px] items-center px-10 pt-10">
      <h3 className="heading-three m-0 w-fit">{t("excel_import.import_signs")}</h3>

      <div className="m-auto mb-4 flex h-full flex-col items-center justify-center">
        <div className="flex items-center">
          <div
            className={cn("flex flex-col items-center justify-center", {
              "hover:cursor-pointer": clickable,
            })}
            onClick={() => clickable && setStep(0)}
          >
            <div
              className={cn("flex h-6 w-6 items-center justify-center rounded-full", {
                "bg-primary": chooseDesign && !disabled,
                "border-2 border-primary": importSigns,
                "bg-white-20": disabled,
              })}
            >
              {(chooseDesign || disabled) && (
                <div
                  className={cn("h-5 w-5 rounded-full border-2 border-white", {
                    "bg-primary": chooseDesign,
                    "bg-white-20": disabled,
                  })}
                ></div>
              )}
              {importSigns && <CheckmarkSmall className="absolute h-6 w-6 text-primary" />}
            </div>
            <div className="paragraph-2xs absolute mb-0 w-fit translate-y-6">{t("excel_import.choose_design")}</div>
          </div>

          <div
            className={cn("h-0.5 w-56", {
              "bg-white-20": chooseDesign,
              "bg-primary": importSigns,
            })}
          ></div>

          <div
            className={cn("flex flex-col items-center justify-center", {
              "hover:cursor-pointer": clickable,
            })}
            onClick={() => clickable && setStep(1)}
          >
            <div
              className={cn("flex h-6 w-6 items-center justify-center rounded-full", {
                "bg-primary": importSigns,
                "border-2 border-white-20": chooseDesign,
              })}
            >
              {importSigns && <div className={cn("h-5 w-5 rounded-full border-2 border-white bg-primary")}></div>}
            </div>
            <div className="paragraph-2xs absolute mb-0 w-fit translate-y-6">{t("excel_import.import_signs")}</div>
          </div>
        </div>
      </div>

      <h3 className="heading-three invisible m-0 w-fit">{t("excel_import.import_signs")}</h3>

      {!restrictClose && (
        <button
          className="excel-import-close button button-large button-subtle icon-only no-focus absolute right-8 top-6 mb-0"
          onClick={() => handleClose()}
          type="button"
        >
          <CloseLarge />
        </button>
      )}
    </div>
  )
}

interface HowItWorksProps {
  showVideo: boolean
  toggleVideo: () => void
  maxRows: number
  classes?: string
}

const HowItWorks: React.FC<HowItWorksProps> = ({ showVideo, toggleVideo, maxRows, classes }) => {
  const { t } = useTranslation()

  return (
    <div className={cn("rounded-lg border-1 border-blue-200 bg-blue-100 p-6", classes)}>
      <h5 className="heading-five mb-3">{t("excel_import.explanation")}</h5>

      <div className="mb-1 flex">
        <div className="my-2 mr-2 h-1 w-1 min-w-[4px] rounded-full bg-black"></div>
        <span className="text-sm">{t("excel_import.explanation_spec", { count: maxRows })}</span>
      </div>
      <div className="mb-1 flex">
        <div className="my-2 mr-2 h-1 w-1 min-w-[4px] rounded-full bg-black"></div>
        <span className="text-sm">{t("excel_import.explanation_objects")}</span>
      </div>
      <div className="mb-4 flex">
        <div className="my-2 mr-2 h-1 w-1 min-w-[4px] rounded-full bg-black"></div>
        <span className="text-sm">{t("excel_import.explanation_steps")}</span>
      </div>

      <a onClick={toggleVideo} className="flex w-fit items-center" href="#">
        {showVideo ? <CloseLarge className="mr-2 h-6 w-6" /> : <PlayFill className="mr-2 h-6 w-6" />}
        <span className="text-sm underline">
          {showVideo ? t("excel_import.hide_video") : t("excel_import.watch_video")}
        </span>
      </a>
    </div>
  )
}

interface VideoProps {
  showVideo: boolean
}

const Video: React.FC<VideoProps> = ({ showVideo }) => {
  return (
    <div className={cn({ "mt-4": showVideo })}>
      {showVideo && (
        <video className="video-js vjs-default-skin h-[252px] w-full" controls preload="auto">
          <source
            src={
              "https://skyltmax-assets.s3-eu-central-1.amazonaws.com/videos/signomatic/file_import/file_import_en-IE.mp4"
            }
            type="video/mp4"
          />
        </video>
      )}
    </div>
  )
}

interface WrapperProps {
  open: boolean
  handleClose: (e?: React.MouseEvent) => void
  children: React.ReactNode
}

const Wrapper: React.FC<WrapperProps> = ({ open, handleClose, children }) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-ink-100 bg-opacity-60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
          ></motion.div>

          <Dialog
            open={open}
            as="div"
            className="excel-import-modal fixed inset-0 z-50 m-auto h-full max-h-[869px] w-full max-w-[1612px]"
            onClose={() => handleClose}
            onClick={handleClose}
          >
            <motion.div
              className="h-full w-full p-12"
              initial={{ translateY: "100vh" }}
              animate={{ translateY: 0 }}
              exit={{ translateY: "100vh" }}
              transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
            >
              <div className="import-dialog relative flex h-full w-full flex-col rounded-xl bg-white-1">{children}</div>
            </motion.div>
          </Dialog>
        </>
      )}
    </AnimatePresence>
  )
}

interface AdminSkipIntroProps {
  skipIntro: boolean
  toggle: () => void
}

const AdminSkipIntro: React.FC<AdminSkipIntroProps> = ({ skipIntro, toggle }) => {
  return (
    <div className="mt-4 flex items-center">
      <input
        id="skip-excel-intro"
        className="mr-1 cursor-pointer appearance-none"
        type="checkbox"
        onChange={toggle}
        checked={skipIntro}
      />
      <label className="checkbox-label paragraph-xs !mb-0" htmlFor="skip-excel-intro">
        Do not show this step again
      </label>
    </div>
  )
}

const running = (status?: GenerationStatus) => {
  return status && status.total && status.total > 0 && !status.saved
}

const allowedTypes = [".xlsx", ".ods", ".csv"]

interface ExcelImportInputs {
  count: string
  file: string
}

interface FileImportModalProps {
  sign: Sign
  closeModal: (e?: React.MouseEvent) => void
  open: boolean
  status?: GenerationStatus
  admin?: Pick<Admin, "id">
}

const FileImportModal: React.FC<FileImportModalProps> = ({ open, closeModal, sign, status, admin }) => {
  const { t } = useTranslation()
  const [validFile, setValidFile] = useState(false)
  const [skipIntro, setSkipIntro] = useState(storageLocal.getItem("skipExcelIntro") == "true")
  const [step, setStep] = useState(skipIntro ? 1 : 0)
  const [showVideo, setShowVideo] = useState(false)
  const [count, setCount] = useState("1")
  const [previewSVG, setPreviewSVG] = useState("")
  const [fileError, setFileError] = useState<string | undefined>()
  const dispatch = useDispatch()
  const [finished, setFinished] = useState(false)

  const [generateMutation, { loading: generateMutationLoading }] = useMutation(generateSignsDoc, {
    context: { hasUpload: true },
  })
  const [cancelMutation, { loading: cancelMutationLoading }] = useMutation(generateSignsAbortDoc, {
    variables: { id: status?.id || "" },
  })

  const formMethods = useForm<ExcelImportInputs>()
  const { register, handleSubmit, reset } = formMethods

  const maxNumberOfFiles = 1
  const maxRows = 500

  const {
    uppy,
    state: uppyState,
    invalidFiles,
    removeInvalidFile,
  } = useUppy("uploader", allowedTypes, true, maxNumberOfFiles)

  const files = useMemo(() => {
    const array: Array<UploadedUppyFile<any, any> | FailedUppyFile<any, any>> = []

    for (const key in uppyState.files) {
      if (Object.prototype.hasOwnProperty.call(uppyState.files, key)) {
        const file = uppyState.files[key]
        invariant(file)
        array.push(file)
      }
    }

    return array
  }, [uppyState.files])

  const toggleVideo = useCallback(() => {
    setShowVideo(!showVideo)
  }, [showVideo])

  const toggleAdminSkipIntro = useCallback(() => {
    const newValue = !skipIntro
    setSkipIntro(newValue)
    storageLocal.setItem("skipExcelIntro", newValue.toString())
  }, [skipIntro])

  const onCountChange = useCallback(
    (c: string) => {
      if (c == "") {
        setCount(c)
      } else {
        setCount(parseInt(c).toString())
      }
    },
    [setCount]
  )

  const onFileAdded = useCallback(() => {
    setValidFile(true)
  }, [])

  const onFileRemoved = useCallback(() => {
    reset()
    setValidFile(false)
    setFileError(undefined)
  }, [reset])

  const exampleImage = () => {
    const textObjCount = sign.allObjects.filter(obj => Sign.isText(obj.state)).length

    switch (textObjCount) {
      case 1:
        return Images["one_text_object"]
      case 3:
        return Images["three_text_objects"]
      default:
        return Images["two_text_objects"]
    }
  }

  useEffect(() => {
    const loadSvg = async () => {
      const svg = await sign.toSVG()

      if (svg && svg[0]) {
        setPreviewSVG(svg[0])
      }
    }

    if (open) {
      loadSvg()
    }
  }, [sign, open])

  const restrictClose = step == 2 && !admin

  const handleClose = useCallback(
    (e?: React.MouseEvent) => {
      if (!restrictClose) {
        closeModal(e)
        return
      }
    },
    [restrictClose, closeModal]
  )

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !restrictClose) {
        closeModal()
        return
      }
    }

    document.addEventListener("keydown", handler)

    return () => document.removeEventListener("keydown", handler)
  }, [restrictClose, closeModal])

  useEffect(() => {
    if (!finished && status?.saved) {
      if (files[0]) {
        uppy.removeFile(files[0].id)
      }
      reset()

      setStep(skipIntro ? 1 : 0)
      setFinished(true)
    }
  }, [status?.saved, admin, setStep, uppy, files, step, finished, reset, skipIntro])

  const textContents: string[] = sign.allObjects
    .filter(obj => Sign.isText(obj.state))
    .map(obj => (obj as SignText).text || "")

  const submit: SubmitHandler<ExcelImportInputs> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      const result = await generateMutation({
        variables: {
          fileData: JSON.parse(form.file),
          type: sign.signType || "",
          count: parseInt(count),
          template: JSON.stringify(await sign.signcode(false)),
        },
      })

      const data = result.data?.generateSigns

      if (data?.status) {
        setStep(2)
        setFinished(false)
      }

      if (data?.errors && data.errors[0]) {
        setFileError(data.errors[0].message)
      }
    },
    [count, generateMutation, sign, setFileError]
  )

  const fixmethodMissing = !sign.state || !sign.state.fixmethod
  const textMissing = textContents.length == 0

  if (fixmethodMissing || textMissing) {
    return (
      <Wrapper open={open} handleClose={handleClose}>
        <Header step={0} setStep={setStep} handleClose={handleClose} disabled />

        <div className="smth-missing flex h-full w-full flex-col items-center justify-center overflow-scroll p-10">
          <HowItWorks
            showVideo={showVideo}
            toggleVideo={toggleVideo}
            maxRows={maxRows}
            classes={showVideo ? "mt-20" : ""}
          />

          <Video showVideo={showVideo} />

          <div className="paragraph-sm mt-6 w-fit">
            {textMissing && fixmethodMissing && t("excel_import.text_fixmethod_required")}
            {textMissing && !fixmethodMissing && t("excel_import.text_object_required")}
            {!textMissing && fixmethodMissing && t("excel_import.fixmethod_required")}
          </div>
          <button
            className="button button-secondary button-medium mb-0"
            onClick={() => {
              dispatch(actions.setToolbox(textMissing ? "text" : "fixmethod"))
              closeModal()
            }}
            type="button"
          >
            {textMissing ? t("excel_import.text_object_button") : t("excel_import.fixmethod_button")}
          </button>
        </div>
      </Wrapper>
    )
  }

  const downloadTemplate = () => {
    fetch("/excel_import/template", {
      body: JSON.stringify({ texts: JSON.stringify(textContents) }),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
    })
      .then(resp => (resp.status === 200 ? resp.blob() : Promise.reject(`template response status ${resp.status}`)))
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.download = "template.xlsx"
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(e => {
        console.error(e)
        SentryReact.captureException(e)
      })
  }

  return (
    <Wrapper open={open} handleClose={handleClose}>
      <Header restrictClose={restrictClose} step={step} setStep={setStep} handleClose={handleClose} />

      <FormForm
        id="excel-import"
        className="flex h-full w-full"
        noValidate
        onSubmit={handleSubmit(submit)}
        formMethods={formMethods}
      >
        <div className="relative flex h-full w-full overflow-hidden">
          <div
            className={cn("absolute left-0 top-0 flex w-[300%] transition-[left] duration-500", {
              "h-full": [0, 2].includes(step),
              "h-[calc(100%-81px)]": step == 1,
            })}
            style={{ left: `${step * -100}%` }}
          >
            <div className="step-0 flex h-fit max-h-full w-1/3 justify-center gap-12 overflow-y-auto px-10 py-6">
              <div
                className={cn("left-column flex w-full max-w-[448px] flex-col items-end justify-center", {
                  "h-fit": showVideo,
                })}
              >
                <HowItWorks showVideo={showVideo} toggleVideo={toggleVideo} maxRows={maxRows} />

                <Video showVideo={showVideo} />

                {admin && <AdminSkipIntro skipIntro={skipIntro} toggle={toggleAdminSkipIntro} />}
              </div>

              <div className="right-column flex h-fit w-full max-w-[448px] flex-col justify-start">
                <div className="mb-4 flex h-[354px] items-center justify-center rounded-lg border-1 border-white-20 bg-white-5">
                  <SVGInline
                    svg={previewSVG || ""}
                    className="signv2 flex h-full w-full items-center justify-center [&>svg]:max-h-[calc(100%_-_44px)] [&>svg]:max-w-[calc(100%_-_44px)] [&>svg]:overflow-visible [&_.cutline]:hidden"
                  />
                </div>

                <div className="paragraph-sm mb-0">{t("excel_import.check_design")}</div>
                <div className="paragraph-sm bold mb-4">{t("excel_import.confirm_design")}</div>

                <div className="flex gap-3">
                  <button
                    className="button button-secondary button-medium m-0"
                    type="button"
                    onClick={() => closeModal()}
                  >
                    <EditOutline /> {t("excel_import.edit_design")}
                  </button>
                  <button
                    className="next-step button button-primary button-medium icon-right !m-0"
                    type="button"
                    onClick={() => setStep(1)}
                  >
                    {t("excel_import.next_step")} <ArrowRight />
                  </button>
                </div>
              </div>
            </div>

            <div className="step-1 flex h-fit max-h-full w-1/3 gap-8 overflow-y-auto px-10 py-6">
              <div
                className={cn("left-column flex w-full flex-col justify-start pt-8", {
                  "h-fit": showVideo,
                })}
              >
                <div className="self-end">
                  <p className="bold mb-2 text-base font-normal">{t("excel_import.excel_steps")}</p>
                  <p className="mb-3 text-sm font-normal">{t("excel_import.excel_steps_help")}</p>

                  <div className="mb-1 flex">
                    <div className="my-2 mr-2 h-1 w-1 rounded-full bg-black"></div>
                    <span className="text-sm">{t("excel_import.excel_step_1")}</span>
                  </div>
                  <div className="mb-1 flex">
                    <div className="my-2 mr-2 h-1 w-1 rounded-full bg-black"></div>
                    <span className="text-sm">{t("excel_import.excel_step_2")}</span>
                  </div>
                  <div className="mb-4 flex">
                    <div className="my-2 mr-2 h-1 w-1 rounded-full bg-black"></div>
                    <span className="text-sm">{t("excel_import.excel_step_3", { rows: maxRows })}</span>
                  </div>

                  <img className="mb-1 max-h-40 w-fit" src={exampleImage()} alt="" />

                  <p className="paragraph-2xs mb-4">{t("excel_import.excel_example")}</p>
                  <a onClick={toggleVideo} className="flex w-fit items-center" href="#">
                    {showVideo ? <CloseLarge className="mr-2 h-6 w-6" /> : <PlayFill className="mr-2 h-6 w-6" />}
                    <span className="text-sm underline">
                      {showVideo ? t("excel_import.hide_video") : t("excel_import.watch_video")}
                    </span>
                  </a>

                  <Video showVideo={showVideo} />
                </div>
              </div>

              <div className="right-column h-fit w-full">
                <div className="mb-4 rounded-lg border-1 border-white-20 bg-white p-6">
                  <h4 className="heading-four mb-4">1. {t("excel_import.step_1")}</h4>

                  <button type="button" onClick={downloadTemplate} className="button button-primary button-medium !m-0">
                    <Download />
                    {t("excel_import.download_template")}
                  </button>
                </div>

                <div className="rounded-lg border-1 border-white-20 bg-white p-6">
                  <h4 className="heading-four mb-4">2. {t("excel_import.step_2")}</h4>

                  <p className="paragraph-xs bold mb-1">{t("excel_import.count")}</p>
                  <p className="paragraph-xs mb-2">{t("excel_import.change_count_after")}</p>

                  <input
                    type="text"
                    name="excel_import_count"
                    value={count}
                    className="mb-3 w-48"
                    onChange={e => onCountChange(e.target.value)}
                  />

                  <p className="paragraph-xs bold mb-1">{t("excel_import.select_file")}</p>
                  <p className="paragraph-xs mb-3">{t("excel_import.formats", { rows: maxRows })}</p>

                  <Uploader
                    uppy={uppy}
                    files={files}
                    invalidFiles={invalidFiles}
                    maxNumberOfFiles={maxNumberOfFiles}
                    formRegister={register}
                    name="file"
                    onSuccess={onFileAdded}
                    onRemove={onFileRemoved}
                    removeInvalidFile={removeInvalidFile}
                    formError={fileError}
                  />
                </div>
              </div>
            </div>

            <div className="step-2 flex w-1/3 flex-1 flex-col items-center justify-center">
              <h4 className="heading-four mb-6 w-fit">
                {t("excel_import.progress", { done: status?.done, total: status?.total })}
              </h4>

              <div className="flex">
                <div className="mb-4 h-2 w-[640px] rounded bg-white-20"></div>
                <div
                  className="absolute mb-4 h-2 rounded bg-primary"
                  style={{
                    width: `${((status?.done || 0) / (status?.total || 0)) * 640}px`,
                  }}
                ></div>
              </div>

              <p className="mb-1 w-fit text-sm font-normal">
                {t("fileimport.etd")}: {status?.remaining}
              </p>
              <p className="mb-4 w-fit text-sm font-normal">{t("excel_import.window_open")}</p>
              <button
                className="button button-secondary button-medium m-0"
                type="button"
                onClick={() => {
                  cancelMutation()
                  setStep(1)
                }}
                disabled={cancelMutationLoading}
              >
                {t("excel_import.abort")}
              </button>
            </div>
          </div>

          <div
            className={cn("footer absolute bottom-0 flex w-full justify-between", {
              "border-t-1 border-white-20 px-10 py-5": step == 1,
            })}
          >
            {step == 1 && (
              <>
                <button className="button button-secondary button-medium m-0" type="button" onClick={() => setStep(0)}>
                  <ArrowLeft /> {t("excel_import.previous_step")}
                </button>

                <button
                  className={cn("button button-primary button-medium upload-btn m-0", {
                    "icon-right": !generateMutationLoading,
                  })}
                  disabled={!validFile || generateMutationLoading || !!fileError || count == ""}
                  type="submit"
                >
                  {generateMutationLoading ? (
                    <LoadingAnimation />
                  ) : (
                    <React.Fragment>
                      {t("excel_import.start")} <ArrowRight />
                    </React.Fragment>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </FormForm>
    </Wrapper>
  )
}

interface FileImportProps {
  sign: Sign
  orderId: string
}

const FileImport: React.FC<FileImportProps> = ({ sign, orderId }) => {
  const fileImportOpen = useSelector<State, boolean>(({ fileImportOpen }) => fileImportOpen)
  const { loading, error, data, subscribeToMore } = useQuery(generationStatusDoc, {
    variables: { id: orderId },
  })
  const unsubscribe = useRef<(() => void) | null>(null)
  const dispatch = useDispatch()

  const generationStatus = data?.generationStatus

  const currentAdminQuery = useQuery(currentAdminDoc)
  const currentAdmin = currentAdminQuery.data?.currentAdmin
  const admin = !!currentAdmin?.id

  const closeModal = useCallback(
    (e?: React.MouseEvent) => {
      if (
        e?.target instanceof Element &&
        (e.target.closest(".import-dialog") || e.target.closest(".react-select__option"))
      ) {
        return
      }

      dispatch(actions.closeFileImport())
    },
    [dispatch]
  )

  const [setBasketState] = useMutation(setBasketStateDoc)

  useEffect(() => {
    if (!loading && !error && !unsubscribe.current && running(generationStatus)) {
      unsubscribe.current = subscribeToMore({
        document: generationStatusSubDoc,
        variables: { id: orderId },
      })
    }
  }, [loading, error, generationStatus, subscribeToMore, orderId])

  useEffect(() => {
    if (generationStatus?.saved) {
      sign.changesCommited()
      closeModal()
      setBasketState({ variables: { open: true, reload: true, excelImportNotice: true } })

      if (unsubscribe.current) {
        unsubscribe.current()
        unsubscribe.current = null
      }
    }
  }, [generationStatus?.saved, closeModal, setBasketState, sign])

  if (error) {
    return null
  }

  if (loading && !fileImportOpen) {
    return null
  }

  return createPortal(
    <FileImportModal
      open={fileImportOpen}
      sign={sign}
      closeModal={closeModal}
      status={generationStatus}
      admin={admin ? currentAdmin : undefined}
    />,
    document.body
  )
}

export default FileImport
